import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { STATES, JOURNEY_OPTIONS, SMS_CONSENT } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0a101f] border border-[#22304e] text-white placeholder-[#5c6a8c] px-4 py-3 text-sm focus:outline-none focus:border-[#6b9be8] transition-colors";

const RegistrationForm = ({ idPrefix = "form" }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    state: "",
    journey: "",
    smsConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.firstName || !data.lastName || !data.email || !data.phone || !data.state) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(data.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, data);
      toast.success("Seat reserved! See you at the live briefing.");
      navigate("/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid={`${idPrefix}-registration-form`}>
      <div className="grid grid-cols-2 gap-3">
        <input
          className={inputCls}
          placeholder="First name"
          value={data.firstName}
          onChange={(e) => set("firstName", e.target.value)}
          data-testid={`${idPrefix}-first-name`}
        />
        <input
          className={inputCls}
          placeholder="Last name"
          value={data.lastName}
          onChange={(e) => set("lastName", e.target.value)}
          data-testid={`${idPrefix}-last-name`}
        />
      </div>
      <input
        className={inputCls}
        type="email"
        placeholder="Email address"
        value={data.email}
        onChange={(e) => set("email", e.target.value)}
        data-testid={`${idPrefix}-email`}
      />
      <input
        className={inputCls}
        type="tel"
        placeholder="Mobile phone"
        value={data.phone}
        onChange={(e) => set("phone", e.target.value)}
        data-testid={`${idPrefix}-phone`}
      />
      <Select value={data.state} onValueChange={(v) => set("state", v)}>
        <SelectTrigger className="w-full bg-[#0a101f] border-[#22304e] text-left rounded-none text-sm h-auto py-3 px-4 text-white data-[placeholder]:text-[#5c6a8c] focus:ring-0 focus:border-[#6b9be8]" data-testid={`${idPrefix}-state`}>
          <SelectValue placeholder="State *" />
        </SelectTrigger>
        <SelectContent className="bg-[#0e1526] border-[#22304e] text-white">
          {STATES.map((s) => (
            <SelectItem key={s} value={s} className="focus:bg-[#1a2540] focus:text-white">{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={data.journey} onValueChange={(v) => set("journey", v)}>
        <SelectTrigger className="w-full bg-[#0a101f] border-[#22304e] text-left rounded-none text-sm h-auto py-3 px-4 text-white data-[placeholder]:text-[#5c6a8c] focus:ring-0 focus:border-[#6b9be8]" data-testid={`${idPrefix}-journey`}>
          <SelectValue placeholder="Where are you in your homebuying journey? (optional)" />
        </SelectTrigger>
        <SelectContent className="bg-[#0e1526] border-[#22304e] text-white">
          {JOURNEY_OPTIONS.map((s) => (
            <SelectItem key={s} value={s} className="focus:bg-[#1a2540] focus:text-white">{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label className="flex items-start gap-3 cursor-pointer pt-1">
        <Checkbox
          checked={data.smsConsent}
          onCheckedChange={(v) => set("smsConsent", !!v)}
          className="mt-0.5 border-[#3a4a6e] data-[state=checked]:bg-[#3f639f] data-[state=checked]:border-[#3f639f] rounded-none"
          data-testid={`${idPrefix}-sms-consent`}
        />
        <span className="text-[10px] leading-relaxed text-[#7f8db0]">
          {SMS_CONSENT} View our{" "}
          <Link to="/privacy-policy" className="underline text-[#9db8e8] hover:text-white">Privacy Policy</Link> and{" "}
          <Link to="/terms-conditions" className="underline text-[#9db8e8] hover:text-white">Terms and Conditions</Link>.
        </span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${idPrefix}-submit`}
        className="w-full flex items-center justify-center gap-2 bg-[#3f639f] hover:bg-[#4f76b8] disabled:opacity-60 text-white font-mono text-xs tracking-[0.25em] uppercase py-4 transition-colors cta-glow"
      >
        <Check size={15} /> {submitting ? "Reserving..." : "Reserve My Free Seat"}
      </button>
      <p className="text-center text-[10px] text-[#5c6a8c] pt-1">
        Free educational event · No obligation · No hard credit pull required to attend
      </p>
    </form>
  );
};

export default RegistrationForm;
